import type { RuleRecord } from "./ruleBlocksData";

const record = (value: unknown): value is RuleRecord => !!value && typeof value === "object" && !Array.isArray(value);
const indexOf = (value: unknown): number | null => (typeof value === "number" || typeof value === "string" && /^\d+$/.test(value)) && Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;
const scalar = (value: unknown): string | null => typeof value === "string" || typeof value === "number" && Number.isFinite(value) ? String(value) : null;
const integerText = (value: unknown) => { const text = scalar(value); return text !== null && /^\d+$/.test(text) ? text : null; };

export interface RuleCheck {
	index: number;
	selected: boolean;
	hasResult: boolean;
	matched: boolean | null;
	value: string | null;
	countMax: string | null;
	status: string | null;
	from: string | null;
	to: string | null;
	matchingJoin: string | null;
	filters: { field: string; value: string }[];
}

// This is an optional presentation of known fields; the original record stays intact.
export const ruleChecks = (row: RuleRecord): RuleCheck[] => {
	const snapshot = record(row.rule_snapshot) ? row.rule_snapshot : null;
	const scopes: unknown[] = Array.isArray(snapshot?.scope) ? snapshot.scope : [];
	if (row.scope_results !== undefined && !Array.isArray(row.scope_results)) return [];
	const results = new Map<number, RuleRecord>();
	for (const result of (row.scope_results ?? []) as unknown[]) {
		if (!record(result)) return [];
		const index = indexOf(result.scopeIndex ?? result.scope_index);
		// An ambiguous index cannot be safely joined to a definition.
		if (index === null || results.has(index)) return [];
		results.set(index, result);
	}
	const indices = [...new Set([...scopes.map((_, index) => index), ...results.keys()])].sort((a, b) => a - b);
	return indices.map(index => {
		const scope = record(scopes[index]) ? scopes[index] : null;
		const aggregate = scope?.agregator ?? scope?.aggregator;
		const matching = scope?.matching;
		const fields = record(matching) && Array.isArray(matching.fields) ? matching.fields : [];
		const statuses = fields.filter(field => record(field) && field.field === "status");
		const status = statuses.length === 1 && record(statuses[0]) && (fields.length === 1 || record(matching) && matching.join === "AND") ? scalar(statuses[0].against) : null;
		const result = results.get(index);
		const period = record(scope?.period) ? scope.period : null;
		return {
			index, selected: indexOf(row.scope_index) === index, hasResult: !!result,
			matched: typeof result?.matched === "boolean" ? result.matched : null,
			value: scalar(result?.value), countMax: record(aggregate) && aggregate.type === "count" ? integerText(aggregate.max) : null,
			status, from: scalar(period?.from), to: scalar(period?.to),
			matchingJoin: record(matching) ? scalar(matching.join) : null,
			filters: fields.flatMap(field => record(field) && typeof field.field === "string" && scalar(field.against) !== null ? [{ field: field.field, value: scalar(field.against)! }] : []),
		};
	});
};

export const ruleCheckExcess = (check: RuleCheck): string | null => {
	if (check.matched !== true || check.countMax === null || check.value === null || !/^\d+$/.test(check.value)) return null;
	const excess = BigInt(check.value) - BigInt(check.countMax);
	return excess > 0n ? excess.toString() : null;
};
